import { Any, HubUserCredentials, IDENTITY_TOKEN_INVALID_ERROR } from "@cede/types";
import type { ServerApiError } from "@cede/types";
import { CedeSDKError, Rfc9457Error } from "../errors";
import * as cedeSdkErrors from "../errors/errors";
import { Logger } from "../logger";
import { advancedRandomId } from "../random";
import { NodeEnv } from "../trade/campaigns/getCampaigns";
import { AppSource } from "./app-source";
import { isServerApiError, isServerApiRfcError, Rfc9457ProblemDetails } from "./endpoints";
import { currentIdentityToken, markIdentityTokenFailed, refreshStaleIdentityToken } from "./identity-token-refresher";
import type { APIResponseMap } from "./types";
import type { Scope } from "@sentry/types";

// Schema mapping for automatic parsing - maps endpoints to their Zod schemas
type ZodSchema<T> = {
  safeParse: (data: unknown) => { success: boolean; data?: T; error?: unknown };
};

export type EndpointSchemaMap = Partial<{
  [K in keyof APIResponseMap]: ZodSchema<APIResponseMap[K]>;
}>;

export interface ApiClientConfig {
  apiUrl: string;
  apiToken: string;
  nodeEnv: NodeEnv;
  onCorrelationIdGenerated?: (id: string) => void;
  appSource?: AppSource;
}

export type RequestParams = {
  pathParams?: string[];
  queryParams?: Record<string, string>;
  bodyParams?: Record<string, Any>;
  /** Multipart body; takes the place of `bodyParams`. The browser sets the boundary content-type. */
  formData?: FormData;
  /** Overrides the default request deadline, e.g. for an upload on a slow connection. */
  timeoutMs?: number;
  headers?: Record<string, string>;
  /**
   * Lets the request outlive the page, for unload-time sends. The browser caps the
   * combined body of all in-flight keepalive requests at 64 KB — callers must bound
   * their payload themselves; over the cap the browser rejects the fetch outright.
   */
  keepalive?: boolean;
};

type ApiHelperHook = (response: Response, fetcher: () => Promise<Response>) => Promise<Response>;

/**
 * Each entry is ONE path segment, so its reserved characters must be escaped or
 * they restructure the URL. Paper position triggers carry the pair symbol inside
 * `externalOrderId` (`PosTrig-<account>-BTC/USDC:USDC-tp-<price>`), whose slash
 * otherwise splits the segment and the route stops matching.
 */
function encodePathParams(pathParams: string[]): string {
  return pathParams.map((param) => encodeURIComponent(param)).join("/");
}

const REQUEST_TIMEOUT_MS = 15_000;
const RETRY_BACKOFF_MS = 150;
const RETRYABLE_NETWORK_CODES = new Set([
  "ECONNRESET",
  "EPIPE",
  "ETIMEDOUT",
  "UND_ERR_SOCKET",
  "UND_ERR_CONNECT_TIMEOUT",
  "UND_ERR_HEADERS_TIMEOUT",
]);

type RetryableError = Error & {
  code?: unknown;
  cause?: unknown;
};

function getErrorCode(error: unknown, depth = 0): string | undefined {
  if (!error || typeof error !== "object" || depth > 4) {
    return undefined;
  }

  const retryableError = error as RetryableError;
  if (retryableError.code !== undefined) {
    return String(retryableError.code);
  }

  return getErrorCode(retryableError.cause, depth + 1);
}

export type ApiRequestErrorContext = {
  /** `API_ENDPOINT` value, hash suffix stripped. Stable, carries no query params. */
  endpoint: string;
  method: string;
  /** True when the request hit its deadline rather than failing outright. */
  isTimeout: boolean;
  correlationId?: string;
  originalErrorCode?: string;
  details?: string;
  /** The server's HTTP status when it answered with an error body; absent on a transport failure. */
  statusCode?: number;
  /** The server body's `error` field, e.g. `IdentityTokenInvalidError`. */
  errorType?: string;
};

/**
 * Transport failure raised by {@link ApiHelper}. A real class, not a type alias:
 * Sentry's `beforeSend` identifies it via `instanceof` on `hint.originalException`
 * to set a per-endpoint fingerprint, which a structural type cannot support.
 *
 * Carries the endpoint ENUM, never the URL — the URL holds query params (campaign
 * names, wallet addresses) that must not become Sentry issue titles or tags.
 */
export class ApiRequestError extends Error {
  readonly endpoint: string;
  readonly method: string;
  readonly isTimeout: boolean;
  readonly correlationId?: string;
  readonly originalErrorCode?: string;
  readonly details?: string;
  readonly statusCode?: number;
  readonly errorType?: string;

  constructor(message: string, context: ApiRequestErrorContext, options?: { cause?: unknown }) {
    super(message, options as ErrorOptions);
    this.name = "ApiRequestError";
    this.endpoint = context.endpoint;
    this.method = context.method;
    this.isTimeout = context.isTimeout;
    this.correlationId = context.correlationId;
    this.originalErrorCode = context.originalErrorCode;
    this.details = context.details;
    this.statusCode = context.statusCode;
    this.errorType = context.errorType;
    Object.setPrototypeOf(this, ApiRequestError.prototype);
  }
}

/**
 * `instanceof` fails across duplicated module copies (bundler chunk splits, a
 * second `@cede/utils` in the tree), so Sentry integrations match on this instead.
 */
export function isApiRequestError(error: unknown): error is ApiRequestError {
  return (
    error instanceof Error &&
    error.name === "ApiRequestError" &&
    typeof (error as ApiRequestError).endpoint === "string" &&
    typeof (error as ApiRequestError).method === "string"
  );
}

export function isIdentityTokenInvalidError(error: unknown): boolean {
  return isApiRequestError(error) && error.errorType === IDENTITY_TOKEN_INVALID_ERROR;
}

/** `AbortSignal.timeout` surfaces as a `TimeoutError` DOMException in browsers and Node. */
function isTimeoutError(error: unknown): boolean {
  return !!error && typeof error === "object" && (error as { name?: string }).name === "TimeoutError";
}

function isRetryableGetTransportError(error: unknown): boolean {
  if (error instanceof TypeError && error.message === "fetch failed") {
    return true;
  }

  const code = getErrorCode(error);
  return code !== undefined && RETRYABLE_NETWORK_CODES.has(code);
}

function createTimeoutSignal(timeoutMs: number): AbortSignal | undefined {
  if (typeof AbortSignal === "undefined" || typeof AbortSignal.timeout !== "function") {
    return undefined;
  }
  return AbortSignal.timeout(timeoutMs);
}

async function waitBeforeRetry(): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, RETRY_BACKOFF_MS));
}

function isPromiseLike<T>(value: T | Promise<T>): value is Promise<T> {
  return typeof (value as Promise<T>)?.then === "function";
}

async function fetchWithRetry(
  method: "GET" | "POST" | "DELETE" | "PUT" | "PATCH",
  fetcher: () => Promise<Response>,
): Promise<Response> {
  try {
    return await fetcher();
  } catch (error) {
    if (method !== "GET" || !isRetryableGetTransportError(error)) {
      throw error;
    }
    await waitBeforeRetry();
    return fetcher();
  }
}

export class ApiHelper {
  private config: ApiClientConfig;
  private postRequestHooks: ApiHelperHook[] = [];
  private endpointSchemas: EndpointSchemaMap = {};
  private pendingSchemas: Promise<void> | null = null;

  constructor(config: ApiClientConfig, endpointSchemas?: EndpointSchemaMap) {
    if (!config.apiUrl && config.nodeEnv !== "test") {
      throw new Error("ApiClientConfig must include apiUrl");
    }
    this.config = config;
    this.endpointSchemas = endpointSchemas || {};
  }

  /**
   * Installs (or replaces) the response schemas after construction, for a client
   * built without them so the schema map can be loaded later.
   *
   * Given a PROMISE, the map is registered as still loading and every parse waits
   * on it, so a response that lands before the load finishes is still validated.
   * Register it at construction to close that window entirely. A rejected load
   * clears the wait and requests proceed unparsed; the caller owns reporting it.
   */
  public setEndpointSchemas(endpointSchemas: EndpointSchemaMap | Promise<EndpointSchemaMap>): void {
    if (!isPromiseLike(endpointSchemas)) {
      this.pendingSchemas = null;
      this.endpointSchemas = endpointSchemas;
      return;
    }

    const pending = Promise.resolve(endpointSchemas)
      .then((schemas) => {
        this.endpointSchemas = schemas;
      })
      .catch(() => {})
      .finally(() => {
        if (this.pendingSchemas === pending) this.pendingSchemas = null;
      });
    this.pendingSchemas = pending;
  }

  /**
   * Automatically parse response data if a schema is configured for the endpoint
   */
  private async parseResponseIfSchemaExists<E extends keyof APIResponseMap>(
    endpoint: E,
    data: APIResponseMap[E],
    params?: RequestParams,
  ): Promise<APIResponseMap[E]> {
    if (this.pendingSchemas) await this.pendingSchemas;

    const schema = this.endpointSchemas[endpoint];
    if (!schema) {
      return data;
    }

    const parseResult = schema.safeParse(data);
    if (!parseResult.success) {
      const pathParamsString = params?.pathParams ? `/${encodePathParams(params.pathParams)}` : "";
      const queryParamsString = params?.queryParams ? `?${new URLSearchParams(params.queryParams)}` : "";
      // eslint-disable-next-line no-console
      console.error(
        `Invalid data structure from ${endpoint}${pathParamsString}${queryParamsString}`,
        parseResult.error,
      );
      throw new Error(
        `Invalid data structure from ${this.config.apiUrl}/${endpoint}${pathParamsString}${queryParamsString}: ${
          parseResult.error && typeof parseResult.error === "object" && "message" in parseResult.error
            ? (parseResult.error as { message: string }).message
            : "Parsing failed"
        }`,
      );
    }

    return parseResult.data as APIResponseMap[E];
  }

  /**
   * Extracts debug override address from localStorage and returns it as a header object.
   * This allows admin users to debug the application as if they were a different user.
   * The backend auth guard validates admin status and swaps the user context.
   *
   * @returns An object with the X-Debug-Override-Address header if set, otherwise empty object
   */
  private getDebugOverrideHeader(): Record<string, string> {
    // Check if we're in browser environment (SSR safe)
    if (typeof window === "undefined") {
      return {};
    }

    // Check for debug override address in localStorage
    // This key is also used in useDebugOverrideAddress hook
    const debugAddress = localStorage.getItem("cede-override-address-claim-rewards");
    if (debugAddress) {
      return { "X-Debug-Override-Address": debugAddress };
    }

    return {};
  }

  private async _request<E extends keyof APIResponseMap>(
    method: "GET" | "POST" | "DELETE" | "PUT" | "PATCH",
    endpoint: E,
    params?: RequestParams,
    sentryInstance?: Scope,
    throwOnError = false,
  ): Promise<APIResponseMap[E]> {
    if (this.config.nodeEnv === "test" && method === "GET") {
      Logger.log(`[ApiHelper Test] Mocking GET ${endpoint}`, Logger.types.INFO);
      // Loaded on demand: the fixtures are test-only and must not ship in browser bundles.
      const { API_MOCK_DATA } = await import("./mocks");
      const mockData = API_MOCK_DATA[endpoint];
      if (mockData === undefined) {
        throw new Error(`[ApiHelper Test] No mock data found for GET endpoint: ${endpoint}`);
      }
      return Promise.resolve(mockData);
    }

    const requestId = advancedRandomId(3);
    // Strip hash fragment from endpoint BEFORE appending pathParams
    // Hash fragments are used for unique enum values but should not be in the URL path
    const baseEndpoint = endpoint.split("#")[0] ?? endpoint;
    const pathParamsString = params?.pathParams ? `/${encodePathParams(params.pathParams)}` : "";
    const queryParamsString = params?.queryParams ? `?${new URLSearchParams(params.queryParams)}` : "";
    const url = `${this.config.apiUrl}/${baseEndpoint}${pathParamsString}${queryParamsString}`;
    const correlationId = crypto.randomUUID();

    if (correlationId) {
      this.config.onCorrelationIdGenerated?.(correlationId);
    }

    const headers: Record<string, string> = {
      "X-REQUEST-ID": requestId,
      "X-ENDPOINT": endpoint,
      ...(this.config.apiToken ? { "X-TOKEN": this.config.apiToken } : {}),
      ...(this.config.appSource ? { "x-app-source": this.config.appSource } : {}),
      ...(correlationId ? { correlationId } : {}),
      ...(params?.headers || {}),
    };

    let body: string | FormData | undefined = undefined;
    if (params?.formData) {
      body = params.formData;
    } else if (params?.bodyParams) {
      body = JSON.stringify(params.bodyParams);
      headers["Content-Type"] = "application/json";
    }

    try {
      const doFetch = (): Promise<Response> =>
        fetch(url, {
          method: method,
          headers: headers,
          body: body,
          credentials: "include",
          // An unload-time send must not be aborted by our own timer: the page is
          // already going away and there is nobody left to retry it.
          ...(params?.keepalive
            ? { keepalive: true }
            : { signal: createTimeoutSignal(params?.timeoutMs ?? REQUEST_TIMEOUT_MS) }),
        });
      const fetcher = (): Promise<Response> => fetchWithRetry(method, doFetch);
      let response = await fetcher();
      if (this.postRequestHooks.length > 0) {
        response = await this.executePostRequestHooks(response, fetcher);
      }

      // Handle 204 No Content - no body to parse
      if (response.status === 204) {
        return undefined as Any;
      }

      const unwrappedResponse = await response.json();

      if (throwOnError && !response.ok) {
        this._throwServerApiError(unwrappedResponse);
      }

      return unwrappedResponse;
    } catch (error: Any) {
      // If it's an API error, throw it directly
      if (error instanceof Rfc9457Error || error instanceof CedeSDKError) {
        throw error;
      }

      const errorCode = getErrorCode(error);
      const errorCodeDetails = errorCode ? `, Code: ${errorCode}` : "";
      const errorMessage = `[ApiHelper] Request failed ${method} ${url}: ${
        error?.message || error
      }, RequestID: ${requestId}${errorCodeDetails}`;
      Logger.error(errorMessage, { error });
      const apiRequestError = new ApiRequestError(
        `[${method} ${baseEndpoint}] ${error?.message || error || errorMessage}`,
        {
          endpoint: baseEndpoint,
          method,
          isTimeout: isTimeoutError(error),
          correlationId: correlationId || undefined,
          originalErrorCode: errorCode,
          details: `[ApiHelper] Request failed ${method} ${baseEndpoint}, RequestID: ${requestId}${errorCodeDetails}`,
          // Callers branch on the status (a 409, a 428); wrapping must not hide it on `cause`.
          statusCode: typeof error?.statusCode === "number" ? error.statusCode : undefined,
          errorType: typeof error?.errorType === "string" ? error.errorType : undefined,
        },
        { cause: error },
      );
      if (sentryInstance) {
        sentryInstance.captureException(apiRequestError, {
          captureContext: {
            extra: {
              method,
              endpoint: baseEndpoint,
              requestId,
              originalError: error,
              correlationId,
              originalErrorCode: errorCode,
            },
          },
        });
      }
      throw apiRequestError;
    }
  }

  public async get<E extends keyof APIResponseMap>(
    endpoint: E,
    params?: RequestParams,
    throwOnError = false,
  ): Promise<APIResponseMap[E]> {
    return this._request("GET", endpoint, params, undefined, throwOnError);
  }

  public async post<E extends keyof APIResponseMap>(
    endpoint: E,
    params?: RequestParams,
    sentryInstance?: Scope,
    throwOnError = false,
  ): Promise<APIResponseMap[E]> {
    return this._request("POST", endpoint, params, sentryInstance, throwOnError);
  }

  public async delete<E extends keyof APIResponseMap>(
    endpoint: E,
    params?: RequestParams,
    sentryInstance?: Scope,
    throwOnError = false,
  ): Promise<APIResponseMap[E]> {
    // Note: Original deleteToApi stringified the whole params object, not just bodyParams.
    // Adjusting _request or this call if that specific behavior is needed.
    // Assuming bodyParams is correct for DELETE body based on typical REST patterns.
    return this._request("DELETE", endpoint, params, sentryInstance, throwOnError);
  }

  public async put<E extends keyof APIResponseMap>(
    endpoint: E,
    params?: RequestParams,
    sentryInstance?: Scope,
    throwOnError = false,
  ): Promise<APIResponseMap[E]> {
    return this._request("PUT", endpoint, params, sentryInstance, throwOnError);
  }

  public async patch<E extends keyof APIResponseMap>(
    endpoint: E,
    params?: RequestParams,
    sentryInstance?: Scope,
    throwOnError = false,
  ): Promise<APIResponseMap[E]> {
    return this._request("PATCH", endpoint, params, sentryInstance, throwOnError);
  }

  public async getOrThrow<E extends keyof APIResponseMap>(
    endpoint: E,
    params?: RequestParams,
  ): Promise<APIResponseMap[E]> {
    const res = await this.get(endpoint, params, true);
    return this.parseResponseIfSchemaExists(endpoint, res, params);
  }

  public async postOrThrow<E extends keyof APIResponseMap>(
    endpoint: E,
    params?: RequestParams,
    sentryInstance?: Scope,
  ): Promise<APIResponseMap[E]> {
    const res = await this.post(endpoint, params, sentryInstance, true);
    return this.parseResponseIfSchemaExists(endpoint, res, params);
  }

  public async deleteOrThrow<E extends keyof APIResponseMap>(
    endpoint: E,
    params?: RequestParams,
    sentryInstance?: Scope,
  ): Promise<APIResponseMap[E]> {
    const res = await this.delete(endpoint, params, sentryInstance, true);
    return this.parseResponseIfSchemaExists(endpoint, res, params);
  }

  public async putOrThrow<E extends keyof APIResponseMap>(
    endpoint: E,
    params?: RequestParams,
    sentryInstance?: Scope,
  ): Promise<APIResponseMap[E]> {
    const res = await this.put(endpoint, params, sentryInstance, true);
    return this.parseResponseIfSchemaExists(endpoint, res, params);
  }

  public async patchOrThrow<E extends keyof APIResponseMap>(
    endpoint: E,
    params?: RequestParams,
    sentryInstance?: Scope,
  ): Promise<APIResponseMap[E]> {
    const res = await this.patch(endpoint, params, sentryInstance, true);
    return this.parseResponseIfSchemaExists(endpoint, res, params);
  }

  public async getWithAuth<E extends keyof APIResponseMap>(
    auth: HubUserCredentials,
    endpoint: E,
    params?: RequestParams,
  ): Promise<APIResponseMap[E]> {
    return this._withAuth(auth, "getWithAuth", (authHeaders) =>
      this.getOrThrow(endpoint, { ...params, headers: { ...(params?.headers || {}), ...authHeaders } }),
    );
  }

  public async postAuth<E extends keyof APIResponseMap>(
    auth: HubUserCredentials,
    endpoint: E,
    params?: RequestParams,
    sentryInstance?: Scope,
  ): Promise<APIResponseMap[E]> {
    return this._withAuth(auth, "postAuth", (authHeaders) =>
      this.postOrThrow(
        endpoint,
        { ...params, headers: { ...(params?.headers || {}), ...authHeaders } },
        sentryInstance,
      ),
    );
  }

  public async deleteAuth<E extends keyof APIResponseMap>(
    auth: HubUserCredentials,
    endpoint: E,
    params?: RequestParams,
    sentryInstance?: Scope,
  ): Promise<APIResponseMap[E]> {
    return this._withAuth(auth, "deleteAuth", (authHeaders) =>
      this.deleteOrThrow(
        endpoint,
        { ...params, headers: { ...(params?.headers || {}), ...authHeaders } },
        sentryInstance,
      ),
    );
  }

  public async putAuth<E extends keyof APIResponseMap>(
    auth: HubUserCredentials,
    endpoint: E,
    params?: RequestParams,
    sentryInstance?: Scope,
  ): Promise<APIResponseMap[E]> {
    return this._withAuth(auth, "putAuth", (authHeaders) =>
      this.putOrThrow(endpoint, { ...params, headers: { ...(params?.headers || {}), ...authHeaders } }, sentryInstance),
    );
  }

  public async patchAuth<E extends keyof APIResponseMap>(
    auth: HubUserCredentials,
    endpoint: E,
    params?: RequestParams,
    sentryInstance?: Scope,
  ): Promise<APIResponseMap[E]> {
    return this._withAuth(auth, "patchAuth", (authHeaders) =>
      this.patchOrThrow(
        endpoint,
        { ...params, headers: { ...(params?.headers || {}), ...authHeaders } },
        sentryInstance,
      ),
    );
  }

  /** A stale identity token is refreshed once and the request retried once; a second rejection is final. */
  private async _withAuth<T>(
    auth: HubUserCredentials,
    callerName: string,
    send: (authHeaders: Record<string, string>) => Promise<T>,
  ): Promise<T> {
    const accessToken = await auth.getAccessToken();
    if (!accessToken || !auth.identityToken) {
      throw new Error(`Authentication credentials are missing for ${callerName}`);
    }
    const sendWith = (identityToken: string): Promise<T> =>
      send({
        "Authorization": `Bearer ${accessToken}`,
        "identity-token": identityToken,
        "identity-provider": auth.identityProvider,
        ...this.getDebugOverrideHeader(),
      });

    const sentToken = currentIdentityToken(auth.identityToken);
    try {
      return await sendWith(sentToken);
    } catch (error) {
      if (!isIdentityTokenInvalidError(error) || !auth.refreshIdentityToken) throw error;
      const freshToken = await refreshStaleIdentityToken(sentToken, auth.refreshIdentityToken);
      if (freshToken == null) throw error;
      try {
        return await sendWith(freshToken);
      } catch (retryError) {
        if (isIdentityTokenInvalidError(retryError)) markIdentityTokenFailed(freshToken);
        throw retryError;
      }
    }
  }

  public async getFromCdn<E extends keyof APIResponseMap>(
    endpoint: E,
    params?: Omit<RequestParams, "apiBaseUrl">,
  ): Promise<APIResponseMap[E]> {
    // In development, skip CDN and use the local API to avoid race conditions
    // where apiUrl is temporarily overwritten while concurrent requests are in-flight.
    // Also avoids CDN rejecting exchangeIds not yet deployed to production (e.g. hibachi).
    const cdnUrl =
      this.config.apiUrl === "https://api.cedelabs.io" ? " https://cdn-api.cedelabs.io" : this.config.apiUrl;
    const originalApiUrl = this.config.apiUrl;
    this.config.apiUrl = cdnUrl;
    try {
      const result = await this.get(endpoint, params);
      return result;
    } finally {
      this.config.apiUrl = originalApiUrl;
    }
  }

  public addPostRequestHook(hook: ApiHelperHook): ApiHelperHook {
    this.postRequestHooks.push(hook);
    return hook;
  }

  public removePostRequestHook(hook: ApiHelperHook): void {
    this.postRequestHooks = this.postRequestHooks.filter((h) => h !== hook);
  }

  private async executePostRequestHooks(response: Response, fetcher: () => Promise<Response>): Promise<Response> {
    for (const hook of this.postRequestHooks) {
      response = await hook(response, fetcher);
    }
    return response;
  }

  private readonly _apiErrorToClassError: Record<string, () => CedeSDKError> = {
    ...Object.fromEntries(
      Object.entries(cedeSdkErrors).map(([key, value]) => [
        key,
        () => new (value as new (m: string, d?: Any) => CedeSDKError)("" as never, ""),
      ]),
    ),
  };

  private _getErrorFromApiError(error: ServerApiError): Error {
    const mappedError = this._apiErrorToClassError[error.error];
    const errorClass = mappedError ? mappedError().overwriteMessage(error.message) : new Error(error.message);
    Object.assign(errorClass, { statusCode: error.statusCode, errorType: error.error });
    return errorClass;
  }

  private _getErrorFromApiRfcError(error: Rfc9457ProblemDetails): Rfc9457Error {
    return new Rfc9457Error(error);
  }

  private _throwServerApiError(data: unknown): void {
    if (isServerApiError(data)) {
      throw this._getErrorFromApiError(data);
    }
    if (isServerApiRfcError(data)) {
      throw this._getErrorFromApiRfcError(data);
    }

    throw new Error("Unknown server API error");
  }
}
